import type { CurrencyCode, WishMessageId } from "./types";

export type AppLocale = "en" | "uk";
export type LocaleSource = "query" | "path" | "browser" | "fallback";

export interface LocaleResolution {
  locale: AppLocale;
  source: LocaleSource;
}

export interface LocaleCurrency {
  code: CurrencyCode;
  symbol: "$" | "₴";
}

const ENGLISH = {
  metaTitle: "Money on your side — ritual demo",
  metaDescription: "A playful, payment-free market-test demo for the Money Frog and Money Tree rituals.",
  aboutPlayTest: "About this play test",
  brand: "Money Rituals",
  pickRitual: "Pick a ritual",
  moneyFrog: "Money Frog",
  moneyTree: "Money Tree",
  back: "Back",
  funMoneyTitle: "Your fun money",
  funMoneyAmountLabel: "Fun money amount in US dollars",
  quickAmountsLabel: "Quick amount choices",
  letsPlay: "Let’s play",
  tapFive: "Tap 5 times",
  frogAlt: "A friendly jade Money Frog",
  bubbleFieldLabel: "Pop one bubble",
  popBubble: "Pop bubble {number}",
  patFrog: "Pat the frog",
  rituals: "Rituals",
  frogApproved: "Frog-approved",
  splitLabel: "Your playful money split",
  enjoy: "Enjoy",
  keep: "Keep",
  topFive: "Top five",
  keepSetAside: "Keep part set aside?",
  yesKept: "Yes, kept it",
  maybeLater: "Maybe later",
  shakeTree: "Shake the tree",
  treeAlt: "A jade and gold Money Tree",
  tapOrSwipe: "Tap or swipe",
  keepWish: "Keep this wish",
  stepOne: "1 / 2",
  stepTwo: "2 / 2",
  nameTitle: "What should we call you?",
  feedbackTitle: "How was it?",
  ratingLegend: "Choose a rating from 1 to 5",
  rating1: "1 — Not fun",
  rating2: "2 — Meh",
  rating3: "3 — Okay",
  rating4: "4 — Fun",
  rating5: "5 — Loved it",
  comment: "Comment",
  optional: "optional",
  commentPlaceholder: "Anything to add?",
  feedbackDisclosure: "Your rating and optional comment will be sent to improve this test. Don’t include sensitive details. Your saved name is not sent.",
  sendFeedback: "Send feedback",
  sendingFeedback: "Sending…",
  retryFeedback: "Try again",
  continueWithoutSending: "Continue",
  feedbackSendError: "We couldn’t confirm your feedback. Try again or continue.",
  feedbackSent: "Feedback sent.",
  nickname: "Name",
  optionalThisDevice: "optional · this device",
  nicknamePlaceholder: "Your name",
  continue: "Continue",
  done: "Done",
  ritualComplete: "Ritual complete",
  anotherRitual: "Pick another ritual",
  clearProgress: "Clear local progress",
  close: "Close",
  justForFun: "Just for fun",
  privacyTitle: "You make every money choice.",
  privacyFrogTree: "The Frog offers a random playful split. The Tree offers a prosperity wish. Neither checks affordability, predicts money, moves money, or gives financial advice.",
  privacyStorage: "No signup, bank connection, or payment. Exact amounts stay in this tab unless you confirm a keep total on this device. When Analytics is enabled, Google receives usage events plus standard page, browser, and device context. If you send feedback, Firebase stores it under an anonymous technical ID. Saved names and exact money values are never attached.",
  privacyCircle: "The names and counts in the ritual circle are fictional previews; they are not real user activity or a live feed.",
  debugInspectorLabel: "Experiment debug inspector",
  qaInspector: "QA inspector",
  hide: "Hide",
  show: "Show",
  arm: "Arm",
  source: "Source",
  frog: "Frog",
  amountError: "Choose {min}–{max}.",
  patStatus: "Pat {count} of 5.",
  popOne: "Pop one",
  chooseBubble: "Choose a bubble",
  bubblesLive: "Five pats complete. The frog opened its mouth and three bubbles appeared.",
  splitLive: "Playful split: enjoy {enjoy} and keep {keep}. You decide.",
  shakeStatus: "Shake {count} of 3.",
  yourWish: "Your wish",
  wishLive: "Your prosperity wish: {wish}",
  doneFrogMessage: "Enjoy your part. Keep the rest.",
  wishCollected: "Wish collected",
  wishFallback: "May prosperity find you today.",
  circleLabel: "Ritual circle",
  savedNickname: "Saved name: {nickname}",
  savedAs: "Saved as {nickname}",
} as const;

export type CopyKey = keyof typeof ENGLISH;
type CopyCatalog = Record<CopyKey, string>;

const UKRAINIAN: CopyCatalog = {
  metaTitle: "Гроші на твоєму боці — демо ритуалів",
  metaDescription: "Грайливе демо без оплат для тестування ритуалів «Грошова жабка» та «Грошове дерево».",
  aboutPlayTest: "Про цей тест",
  brand: "Money Rituals",
  pickRitual: "Обери ритуал",
  moneyFrog: "Грошова жабка",
  moneyTree: "Грошове дерево",
  back: "Назад",
  funMoneyTitle: "Гроші на приємне",
  funMoneyAmountLabel: "Сума на приємне у гривнях",
  quickAmountsLabel: "Швидкий вибір суми",
  letsPlay: "Граймо",
  tapFive: "Натисни 5 разів",
  frogAlt: "Привітна нефритова Грошова жабка",
  bubbleFieldLabel: "Лусни одну бульбашку",
  popBubble: "Лусни бульбашку {number}",
  patFrog: "Натисни жабу",
  rituals: "Ритуали",
  frogApproved: "Жабка схвалила",
  splitLabel: "Ігровий поділ твоєї суми",
  enjoy: "На приємне",
  keep: "Відкласти",
  topFive: "Топ-5",
  keepSetAside: "Цю частину відкладено?",
  yesKept: "Так, відкладено",
  maybeLater: "Можливо, пізніше",
  shakeTree: "Потруси дерево",
  treeAlt: "Нефритово-золоте Грошове дерево",
  tapOrSwipe: "Торкнися або проведи пальцем",
  keepWish: "Зберегти побажання",
  stepOne: "1 / 2",
  stepTwo: "2 / 2",
  nameTitle: "Як тебе звати?",
  feedbackTitle: "Як тобі?",
  ratingLegend: "Обери оцінку від 1 до 5",
  rating1: "1 — Не сподобалося",
  rating2: "2 — Так собі",
  rating3: "3 — Нормально",
  rating4: "4 — Весело",
  rating5: "5 — Дуже сподобалося",
  comment: "Коментар",
  optional: "необов’язково",
  commentPlaceholder: "Хочеш щось додати?",
  feedbackDisclosure: "Оцінку та необов’язковий коментар буде надіслано для покращення тесту. Не вказуй чутливі дані. Збережене ім’я не надсилається.",
  sendFeedback: "Надіслати відгук",
  sendingFeedback: "Надсилаємо…",
  retryFeedback: "Спробувати ще раз",
  continueWithoutSending: "Продовжити",
  feedbackSendError: "Не вдалося підтвердити надсилання відгуку. Спробуй ще раз або продовж.",
  feedbackSent: "Відгук надіслано.",
  nickname: "Ім’я",
  optionalThisDevice: "необов’язково · лише на цьому пристрої",
  nicknamePlaceholder: "Твоє ім’я",
  continue: "Далі",
  done: "Готово",
  ritualComplete: "Ритуал завершено",
  anotherRitual: "Обрати інший ритуал",
  clearProgress: "Стерти прогрес на цьому пристрої",
  close: "Закрити",
  justForFun: "Лише для розваги",
  privacyTitle: "Усі рішення про гроші — за тобою.",
  privacyFrogTree: "Жабка пропонує випадковий ігровий поділ. Дерево дарує побажання достатку. Вони не перевіряють, що тобі по кишені, не передбачають надходжень, не переказують грошей і не дають фінансових порад.",
  privacyStorage: "Без реєстрації, підключення банку чи оплати. Точні суми залишаються в цій вкладці; якщо ти підтвердиш відкладену суму, на цьому пристрої збережеться лише її загальний підсумок. Коли Analytics увімкнено, Google отримує події використання та стандартні дані про сторінку, браузер і пристрій. Якщо ти надсилаєш відгук, Firebase зберігає його під анонімним технічним ID. Збережене ім’я й точні суми не додаються.",
  privacyCircle: "Імена й числа в ритуальному колі вигадані; це не справжня активність користувачів і не жива стрічка.",
  debugInspectorLabel: "Інспектор експерименту",
  qaInspector: "QA-інспектор",
  hide: "Сховати",
  show: "Показати",
  arm: "Варіант",
  source: "Джерело",
  frog: "Жабка",
  amountError: "Обери суму від {min} до {max}.",
  patStatus: "Натискання: {count} із 5.",
  popOne: "Лусни одну",
  chooseBubble: "Обери бульбашку",
  bubblesLive: "Усі п’ять натискань зараховано. Жабка відкрила рот — з’явилися три бульбашки.",
  splitLive: "Ігровий поділ: {enjoy} — на приємне, {keep} — відкласти. Рішення за тобою.",
  shakeStatus: "Струшування: {count} із 3.",
  yourWish: "Твоє побажання",
  wishLive: "Твоє побажання достатку: {wish}",
  doneFrogMessage: "Насолоджуйся своєю частиною. Решту відклади.",
  wishCollected: "Побажання отримано",
  wishFallback: "Нехай сьогодні до тебе прийде достаток.",
  circleLabel: "Ритуальне коло",
  savedNickname: "Збережене ім’я: {nickname}",
  savedAs: "Збережено як {nickname}",
};

const CATALOGS: Record<AppLocale, CopyCatalog> = {
  en: ENGLISH,
  uk: UKRAINIAN,
};

const WISH_COPY: Record<AppLocale, Record<WishMessageId, string>> = {
  en: {
    "prosperity-grow": "May a small money win grow your prosperity today.",
    "useful-opportunity": "May a timely idea bring you a profit today.",
    "welcome-saving": "May an unexpected discount keep more money with you today.",
    "welcome-reward": "May your effort bring a welcome cash reward today.",
    "lucky-deal": "May a lucky deal bring you a real money benefit today.",
    "money-stretch": "May your money stretch a little further today.",
    "avoided-fee": "May avoiding an unnecessary fee keep more money with you today.",
    "better-value": "May something you need find you at a better price today.",
    "extra-money": "May a small opportunity bring welcome extra money today.",
    "generous-reward": "May a money bonus exceed your expectations today.",
  },
  uk: {
    "prosperity-grow": "Нехай сьогодні маленька грошова перемога примножить твій достаток.",
    "useful-opportunity": "Нехай сьогодні влучна ідея принесе тобі прибуток.",
    "welcome-saving": "Нехай сьогодні несподівана знижка збереже частину твоїх грошей.",
    "welcome-reward": "Нехай сьогодні твої зусилля принесуть приємну грошову винагороду.",
    "lucky-deal": "Нехай сьогодні вдала угода принесе тобі відчутну грошову вигоду.",
    "money-stretch": "Нехай сьогодні твоїх грошей вистачить на трохи більше.",
    "avoided-fee": "Нехай сьогодні тобі вдасться уникнути зайвої комісії й зберегти більше грошей.",
    "better-value": "Нехай сьогодні потрібна покупка дістанеться тобі за кращою ціною.",
    "extra-money": "Нехай сьогодні невелика нагода принесе приємний додатковий заробіток.",
    "generous-reward": "Нехай сьогодні грошовий бонус перевершить твої очікування.",
  },
};

const LANGUAGE_TAGS: Record<AppLocale, string> = {
  en: "en-US",
  uk: "uk-UA",
};

const CURRENCIES: Record<AppLocale, LocaleCurrency> = {
  en: { code: "USD", symbol: "$" },
  uk: { code: "UAH", symbol: "₴" },
};

export function resolveLocale(
  override: string | null,
  browserLanguages: readonly string[],
  pathOverride: AppLocale | null = null,
): LocaleResolution {
  const forcedLocale = localeFromTag(override);
  if (forcedLocale) return { locale: forcedLocale, source: "query" };

  if (pathOverride) return { locale: pathOverride, source: "path" };

  for (const language of browserLanguages) {
    const locale = localeFromTag(language);
    if (locale) return { locale, source: "browser" };
  }

  return { locale: "en", source: "fallback" };
}

export function localeFromPathname(pathname: string): AppLocale | null {
  const finalSegment = pathname.replace(/\/+$/u, "").split("/").pop()?.toLowerCase() ?? "";
  if (finalSegment === "uk" || finalSegment === "uk.html") return "uk";
  if (finalSegment === "en" || finalSegment === "en.html") return "en";
  return null;
}

export function translate(
  locale: AppLocale,
  key: CopyKey,
  params: Readonly<Record<string, string | number>> = {},
): string {
  return CATALOGS[locale][key].replace(/\{([a-z]+)\}/giu, (token, name: string) => {
    const value = params[name];
    return value === undefined ? token : String(value);
  });
}

export function translateWish(locale: AppLocale, wishId: WishMessageId): string {
  return WISH_COPY[locale][wishId];
}

export function localeLanguageTag(locale: AppLocale): string {
  return LANGUAGE_TAGS[locale];
}

export function currencyForLocale(locale: AppLocale): LocaleCurrency {
  return CURRENCIES[locale];
}

export function formatCurrency(locale: AppLocale, minorUnits: number): string {
  const hasFraction = minorUnits % 100 !== 0;
  return new Intl.NumberFormat(localeLanguageTag(locale), {
    style: "currency",
    currency: currencyForLocale(locale).code,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);
}

export function formatPeopleCount(locale: AppLocale, count: number): string {
  const formattedCount = new Intl.NumberFormat(localeLanguageTag(locale)).format(count);
  if (locale === "en") return `${formattedCount} ${count === 1 ? "person" : "people"}`;
  return `${formattedCount} ${ukrainianNoun(count, { one: "людина", few: "людини", many: "людей", other: "людини" })}`;
}

export function formatRitualCount(locale: AppLocale, count: number): string {
  const formattedCount = new Intl.NumberFormat(localeLanguageTag(locale)).format(count);
  if (locale === "en") return `${formattedCount} ${count === 1 ? "ritual" : "rituals"}`;
  return `${formattedCount} ${ukrainianNoun(count, { one: "ритуал", few: "ритуали", many: "ритуалів", other: "ритуалу" })}`;
}

export function normalizeLocalizedDecimal(raw: string): string {
  return raw.trim().replace(",", ".");
}

function localeFromTag(tag: string | null): AppLocale | null {
  if (!tag) return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/u)[0];
  return primary === "uk" || primary === "en" ? primary : null;
}

function ukrainianNoun(
  count: number,
  forms: Readonly<Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }>,
): string {
  return forms[new Intl.PluralRules("uk-UA").select(count)] ?? forms.other;
}
